import {
  Flex,
  Button,
  useColorModeValue,
  useMediaQuery,
  HStack,
  Link,
  Select,
  Text,
  VStack,
  Box,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import {
  FaUser,
  FaGraduationCap,
  FaCode,
  FaBriefcase,
  FaFolderOpen,
  FaEnvelope,
  FaUserCircle,
} from "react-icons/fa";
import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useProfile } from "../hooks/useContent";
import { useAuth } from "../context/AuthContext";

function Nav() {
  const { language, setLanguage } = useLanguage();
  const { data: profile } = useProfile();
  const { user } = useAuth();
  const fullName = profile?.name || "Héctor Durán";

  const [scroll, setScroll] = useState(false);
  const [showName, setShowName] = useState(false);

  const [isLargerThanMD] = useMediaQuery("(min-width: 48em)");

  // Hoisted color-mode values (Rules of Hooks)
  const bg = useColorModeValue("rgba(255,255,255,0.85)", "rgba(13,17,23,0.85)");
  const borderColor = useColorModeValue("text.muted", "whiteAlpha.100");
  const logoColor = useColorModeValue("gray.800", "white");

  const scrollToHero = () => {
    const heroSection = document.querySelector("#hero");
    if (heroSection) heroSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToAbout = () => {
    const aboutSection = document.querySelector("#about");
    if (aboutSection) aboutSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToStudies = () => {
    const studiesSection = document.querySelector("#studies");
    if (studiesSection) studiesSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToSkills = () => {
    const skillsSection = document.querySelector("#skills");
    if (skillsSection) skillsSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToExperience = () => {
    const experienceSection = document.querySelector("#experience");
    if (experienceSection) experienceSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToProjects = () => {
    const projectsSection = document.querySelector("#projects");
    if (projectsSection) projectsSection.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToContact = () => {
    const contactSection = document.querySelector("#contact");
    if (contactSection) contactSection.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const changeScroll = () =>
      document.body.scrollTop > 80 || document.documentElement.scrollTop > 80
        ? setScroll(true)
        : setScroll(false);
    window.addEventListener("scroll", changeScroll);
    return () => window.removeEventListener("scroll", changeScroll);
  }, []);

  const translations = {
    en: {
      about: "About",
      studies: "Studies",
      skills: "Skills",
      experience: "Experience",
      projects: "Projects",
      contact: "Contact",
    },
    es: {
      about: "Sobre mí",
      studies: "Estudios",
      skills: "Habilidades",
      experience: "Experiencia",
      projects: "Proyectos",
      contact: "Contacto",
    },
  };

  const selectedTranslations = translations[language] || translations["en"];

  const navItems = [
    { key: "about", icon: FaUser, label: selectedTranslations.about, onClick: scrollToAbout },
    { key: "studies", icon: FaGraduationCap, label: selectedTranslations.studies, onClick: scrollToStudies },
    { key: "skills", icon: FaCode, label: selectedTranslations.skills, onClick: scrollToSkills },
    { key: "experience", icon: FaBriefcase, label: selectedTranslations.experience, onClick: scrollToExperience },
    { key: "projects", icon: FaFolderOpen, label: selectedTranslations.projects, onClick: scrollToProjects },
    { key: "contact", icon: FaEnvelope, label: selectedTranslations.contact, onClick: scrollToContact },
  ];

  return (
    <>
      <Flex
        bg={bg}
        backdropFilter="blur(10px)"
        px={6}
        h={16}
        boxShadow={scroll ? "md" : "none"}
        borderBottom="1px solid"
        borderColor={borderColor}
        zIndex="sticky"
        position="fixed"
        as="header"
        alignItems={"center"}
        justifyContent={"space-between"}
        w="100%"
        top={0}
      >
        {/* Logo: "HD" — click reveals the full name */}
        <Link
          onClick={() => {
            setShowName((s) => !s);
            scrollToHero();
          }}
          _hover={{ textDecoration: "none" }}
        >
          <HStack spacing={1}>
            <HStack spacing={0}>
              <Text fontWeight="bold" fontSize="xl" color={logoColor} letterSpacing="tight">
                H
              </Text>
              <Text fontWeight="bold" fontSize="xl" color="brand.400" letterSpacing="tight">
                D
              </Text>
            </HStack>
            <Text
              fontWeight="semibold"
              fontSize="md"
              color={logoColor}
              whiteSpace="nowrap"
              overflow="hidden"
              maxW={showName ? "240px" : "0px"}
              opacity={showName ? 1 : 0}
              transition="max-width 0.3s ease, opacity 0.3s ease"
            >
              {fullName}
            </Text>
          </HStack>
        </Link>

        {/* Center links (desktop only) */}
        {isLargerThanMD && (
          <HStack spacing={1}>
            {navItems.map((item) => (
              <Button key={item.key} variant="ghost" size="sm" onClick={item.onClick}>
                {item.label}
              </Button>
            ))}
          </HStack>
        )}

        {/* Right controls: language selector + login (top bar, all screens) */}
        <HStack spacing={2}>
          <Select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            width="80px"
            size="sm"
          >
            <option value="en">EN</option>
            <option value="es">ES</option>
          </Select>

          {/* Auth-aware: si ya hay sesión, lleva al panel (y se resalta) en vez
              de mostrar "Iniciar sesión". Así, al volver con "atrás" al inicio,
              no parece que se cerró la sesión. */}
          <IconButton
            as={RouterLink}
            to={user ? "/admin" : "/login"}
            size="sm"
            variant="ghost"
            color={user ? "brand.400" : undefined}
            icon={<FaUserCircle size={20} />}
            aria-label={
              user
                ? language === "es"
                  ? "Ir al panel"
                  : "Go to panel"
                : language === "es"
                ? "Iniciar sesión"
                : "Sign in"
            }
            borderRadius="full"
          />
        </HStack>
      </Flex>

      {/* Mobile bottom tab bar (small screens only) */}
      {!isLargerThanMD && (
        <Flex
          as="nav"
          bg={bg}
          backdropFilter="blur(12px)"
          border="1px solid"
          borderColor={borderColor}
          position="fixed"
          bottom={4}
          left={4}
          right={4}
          zIndex="sticky"
          borderRadius="2xl"
          boxShadow="0 8px 30px rgba(0,0,0,0.35)"
          justify="space-around"
          align="center"
          py={2}
          px={1}
        >
          {navItems.map((item) => (
            <Box
              key={item.key}
              as="button"
              onClick={item.onClick}
              flex="1"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              px={1}
              aria-label={item.label}
            >
              <VStack spacing={0.5}>
                <Icon as={item.icon} boxSize={4} />
                <Text fontSize="2xs" lineHeight="1" noOfLines={1}>
                  {item.label}
                </Text>
              </VStack>
            </Box>
          ))}
        </Flex>
      )}
    </>
  );
}

export default Nav;
